import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { useAppStore } from 'hooks/state/useAppStore';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { Attribute, Measure, useModelService } from 'services/model/model.service';
import { useEffect, useMemo } from 'react';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ExpandableRow } from 'components/table/ExpandableRow';
import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { createExpanderCell, createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureComparison } from 'components/MeasureComparison';
import { MarketplaceNetSalesRatesInfoContent } from 'pages/insights-pro/marketplace-tab/reports/net-sales-rates/MarketplaceNetSalesRatesInfoContent';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { TbIcon } from 'components/Figma';
import { cn } from 'utils/classNamesTailwind';
import { ComparisonCell } from 'components/table/useDataEntriesInTables';

interface MarketplaceNetSalesRatesRow extends ExpandableRow<MarketplaceNetSalesRatesRow> {
  marketplace: string;
  netSlsRate: number;
  netSlsRateCompare: number;
  slsGmv: number;
  slsGmvCompare: number;
  slsNum: number;
  slsNumCompare: number;
  retGmv: number;
  retGmvCompare: number;
  netSlsGmv: number;
  netSlsGmvCompare: number;
}

const createNetSalesRateCell = (
  context: CellContext<MarketplaceNetSalesRatesRow, ComparisonCell>,
  measure: Measure,
  comparisonActive: boolean
) => (
  <div className="flex justify-between">
    <div className="flex items-center gap-1">
      <TbIcon
        name="Circle"
        className={cn(
          context.getValue().value > 0.8
            ? 'text-data-vis-positive'
            : context.getValue().value > 0.7
              ? 'text-data-vis-warning'
              : 'text-data-vis-critical'
        )}
      />
      <MeasureFormat value={context.getValue().value} unit={measure.metric.unit} />
    </div>

    {comparisonActive && (
      <MeasureComparison
        value={context.getValue().value}
        comparisonValue={context.getValue().compareValue}
        unit={measure.metric.unit}
        interpretation={measure.metric.interpretation}
      />
    )}
  </div>
);

const columnHelper = createColumnHelper<MarketplaceNetSalesRatesRow>();
const buildColumns = (chnMarketplace: Attribute, measures: Measure[], comparisonActive: boolean) => [
  columnHelper.accessor('marketplace', {
    header: () => chnMarketplace.name,
    cell: context => createExpanderCell(context, <>{context.getValue()}</>, true),
  }),
  columnHelper.accessor(row => ({ value: row.netSlsRate, compareValue: row.netSlsRateCompare }), {
    id: measures[0].code,
    header: () => measures[0].metric.name,
    cell: context => createNetSalesRateCell(context, measures[0], comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.slsGmv, compareValue: row.slsGmvCompare }), {
    id: measures[1].code,
    header: () => measures[1].metric.name,
    cell: context => createMeasureCell(context, measures[1], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.slsNum, compareValue: row.slsNumCompare }), {
    id: measures[2].code,
    header: () => measures[2].metric.name,
    cell: context => createMeasureCell(context, measures[2], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retGmv, compareValue: row.retGmvCompare }), {
    id: measures[3].code,
    header: () => measures[3].metric.name,
    cell: context => createMeasureCell(context, measures[3], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.netSlsGmv, compareValue: row.netSlsGmvCompare }), {
    id: measures[4].code,
    header: () => measures[4].metric.name,
    cell: context => createMeasureCell(context, measures[4], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
];

export function MarketplaceNetSalesRatesTable() {
  const { products } = useAppStore();
  const { setContent } = useInfoDrawer();

  const { fetchData } = useProDataService(Service.Marketplaces);
  const requestData = useDataRequest();
  const { getMeasures, getAttributes } = useModelService();

  const measures = useMemo(
    () => getMeasures(['netSlsGmvValRateExpOD', 'slsGmvValOD', 'slsNumOD', 'retGmvValExpOD', 'netSlsGmvValExpOD']),
    [getMeasures]
  );

  const [netSlsGmvValRate, slsGmvVal, slsNum, retGmvVal, netSlsGmvVal] = measures;

  const [chnMarketplace, chnName] = useMemo(() => getAttributes(['chnMarketplace', 'chnName']), [getAttributes]);

  useEffect(() => {
    const infoContent = {
      titleKey: 'insights.marketplace.reports.net_sales_rates',
      subtitleKey: 'info.content.all_dates_utc',
      content: <MarketplaceNetSalesRatesInfoContent />,
    };
    setContent(infoContent);

    return () => {
      setContent(null);
    };
  }, [measures, setContent]);

  const productNumbers = products?.map((p: string) => p);

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData('reports/net-sales-rate-channel-breakdown', {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData?.comparisonTimeFilter?.fromDate && requestData?.comparisonTimeFilter?.toDate
      );
      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-marketplaces-net-sales-rates-table']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const rows =
    reportState.data?.response.sets.marketplaces.map(row => {
      const marketplaceRow: MarketplaceNetSalesRatesRow = {
        marketplace: chnMarketplace.getValue(row.attributes),
        netSlsRate: netSlsGmvValRate.getValue(row.values),
        netSlsRateCompare: netSlsGmvValRate.getValue(row.compareValues),
        slsGmv: slsGmvVal.getValue(row.values),
        slsGmvCompare: slsGmvVal.getValue(row.compareValues),
        slsNum: slsNum.getValue(row.values),
        slsNumCompare: slsNum.getValue(row.compareValues),
        retGmv: retGmvVal.getValue(row.values),
        retGmvCompare: retGmvVal.getValue(row.compareValues),
        netSlsGmv: netSlsGmvVal.getValue(row.values),
        netSlsGmvCompare: netSlsGmvVal.getValue(row.compareValues),
        subRows: [],
      };

      marketplaceRow.subRows = row.sets.channels.map(subRow => {
        const channelRow: MarketplaceNetSalesRatesRow = {
          marketplace: chnName.getValue(subRow.attributes),
          netSlsRate: netSlsGmvValRate.getValue(subRow.values),
          netSlsRateCompare: netSlsGmvValRate.getValue(subRow.compareValues),
          slsGmv: slsGmvVal.getValue(subRow.values),
          slsGmvCompare: slsGmvVal.getValue(subRow.compareValues),
          slsNum: slsNum.getValue(subRow.values),
          slsNumCompare: slsNum.getValue(subRow.compareValues),
          retGmv: retGmvVal.getValue(subRow.values),
          retGmvCompare: retGmvVal.getValue(subRow.compareValues),
          netSlsGmv: netSlsGmvVal.getValue(subRow.values),
          netSlsGmvCompare: netSlsGmvVal.getValue(subRow.compareValues),
          subRows: [],
        };

        return channelRow;
      });

      return marketplaceRow;
    }) ?? [];
  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  const columns = buildColumns(
    chnMarketplace,
    measures,
    isComparisonActive
  ) as ColumnDef<MarketplaceNetSalesRatesRow>[];

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Marketplaces,
      measuresGroup: 'net-sales-rate-channel-breakdown',
      tableName: 'Net Sales Rates Channel Breakdown',
      isProReport: true,
    },
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.marketplace.reports.net_sales_rates.description"
        rows={rows}
        columns={columns}
        exportConfigOptions={exportConfigOptions}
        defaultSortingState={[{ id: netSlsGmvValRate.code, desc: true }]}
        isError={reportState.isError}
        errorMessage={reportState.error}
      />
    </DataTableContextProvider>
  );
}
